import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { absenceService } from '@/api/domains/absence/absence.service';
import type { AbsenceType, AbsenceStatus } from '@/api/domains/absence/absence.types';
import { useHybridExport } from '@/hooks/ui/useHybridExport';
import type { UIExportFormat } from '@/types/fileExport.types';


export interface UseAbsenceScopeExportParams {
  startDate: string;
  endDate: string;
  absenceType: AbsenceType;
  status: AbsenceStatus;
  employeeIds?: string[];
  searchText?: string;
}

export interface UseAbsenceScopeExportResult {
  isExporting: boolean;
  exportAbsences: (format: UIExportFormat) => Promise<void>;
}

/**
 * Descarga del listado administrativo de ausentismos ("Gestión de ausentismos") a Excel.
 * Estrategia híbrida sync/async: el backend responde 200 con el archivo bajo el umbral síncrono,
 * o 202 (job en background) cuando lo supera.
 *
 * Delega el branching sync/202, el estado `isExporting` y las notificaciones en `useHybridExport`;
 * aquí solo se arma el payload con los filtros activos de la pantalla y se traducen los textos.
 */
export function useAbsenceScopeExport(
  params: UseAbsenceScopeExportParams,
): UseAbsenceScopeExportResult {
  const { t: translate } = useTranslation('absence');

  const requestExport = useCallback(
    () => absenceService.exportAbsencesManagement({
      startDate: params.startDate,
      endDate: params.endDate,
      absenceType: params.absenceType,
      status: params.status,
      employeeIds: params.employeeIds?.length ? params.employeeIds.join(',') : null,
      searchText: params.searchText || null,
    }),
    [
      params.startDate,
      params.endDate,
      params.absenceType,
      params.status,
      params.employeeIds,
      params.searchText,
    ],
  );

  const { isExporting, runExport } = useHybridExport({
    requestExport,
    messages: {
      error: translate('scope.messages.exportError'),
      success: translate('scope.messages.exportSuccess'),
      queued: translate('scope.messages.exportQueued'),
    },
  });

  return { isExporting, exportAbsences: runExport };
}

export default useAbsenceScopeExport;
